import { Editor as ToastEditor } from "@toast-ui/react-editor";
import Editor from "components/document/Create/Editor";
import DatePicker from "components/foundation/DatePicker";
import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import React, { useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import DocumentArrayField from "../../DocumentArrayField";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import { TempDocumentDetailResponseWithAny } from "features/document/hooks/queries/useTempDocumentDetailQuery";
import { MINUATES_OF_MEETING_FIELD as FIELD } from "config/documentConstant";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";

/* 회의록 */
interface MinutesOfMeetingForm {
  [FIELD.MEETING_DATE.key]: string;
  [FIELD.LOCATION.key]: string;
  [FIELD.PURPOSE.key]: string;
  [FIELD.ATTENDEES.key]: {
    affiliation: string;
    position: string;
    name: string;
  }[];
  [FIELD.CONTENT.key]: string;
}

interface MinutesOfMeetingProps {
  tempDocumentDetail?: TempDocumentDetailResponseWithAny;
}

const MinutesOfMeeting = ({ tempDocumentDetail }: MinutesOfMeetingProps) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<MinutesOfMeetingForm>();
  const editorRef = useRef<ToastEditor | null>(null);

  useEffect(() => {
    if (editorRef.current && tempDocumentDetail) {
      editorRef.current
        .getInstance()
        .setHTML(tempDocumentDetail.documentData?.[FIELD.CONTENT.key]);
    }
  }, [tempDocumentDetail]);

  useEffect(() => {
    const editorInstance = editorRef.current;

    if (!editorInstance) return;

    const handleChange = () => {
      const value = editorInstance.getInstance().getHTML() || "";
      setValue(FIELD.CONTENT.key, value);
    };

    editorInstance.getInstance().on("change", handleChange);
    return () => editorInstance.getInstance().off("change");
  }, []);

  return (
    <CreateDocumentClassFormBody noBorderTop>
      <CreateDocumentForm.Field label={FIELD.MEETING_DATE.label}>
        <Controller
          name={FIELD.MEETING_DATE.key}
          control={control}
          rules={{
            required: "날짜 기입 필수",
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              usage="document"
              isTimePicker
              placeholder={FIELD.MEETING_DATE.placeholder}
              dateFormat={FIELD.MEETING_DATE.dateFormat}
              defaultValue={
                tempDocumentDetail?.documentData?.[FIELD.MEETING_DATE.key]
              }
              onChangeDate={(_, dateString) => {
                field.onChange(dateString);
              }}
              errorMessage={errors[FIELD.MEETING_DATE.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.LOCATION.label}>
        <DocumentTextField
          {...register(FIELD.LOCATION.key, {
            minLength: {
              value: FIELD.LOCATION.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.LOCATION.minLength,
                FIELD.LOCATION.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.LOCATION.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.LOCATION.minLength,
                FIELD.LOCATION.maxLength,
              ),
            },
          })}
          placeholder={FIELD.LOCATION.placeholder}
          errorMessage={errors[FIELD.LOCATION.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.PURPOSE.label}>
        <DocumentTextField
          {...register(FIELD.PURPOSE.key, {
            minLength: {
              value: FIELD.PURPOSE.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.PURPOSE.minLength,
                FIELD.PURPOSE.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.PURPOSE.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.PURPOSE.minLength,
                FIELD.PURPOSE.maxLength,
              ),
            },
          })}
          placeholder={FIELD.PURPOSE.placeholder}
          errorMessage={errors[FIELD.PURPOSE.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.ATTENDEES.label}>
        <DocumentArrayField
          fieldMetaInfo={
            {
              ...FIELD.ATTENDEES,
              itemFields: FIELD.ATTENDEES.itemFields.map((field) => ({
                ...field,
                required: "참석자 기입 필수",
              })),
            } as any
          }
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.CONTENT.label}>
        <Controller
          name={FIELD.CONTENT.key}
          control={control}
          rules={{
            required: "내용 기입 필수",
          }}
          render={({ field }) => (
            <Editor
              ref={editorRef}
              placeholder="내용을 입력해 주세요."
              defaultValue={
                tempDocumentDetail?.documentData?.[FIELD.CONTENT.key] ||
                field.value
              }
              errorMessage={errors[FIELD.CONTENT.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>
    </CreateDocumentClassFormBody>
  );
};

export default MinutesOfMeeting;
